/**
 * EmmaCapabilitiesExplainer - Phase 8: UX Review (Story 8.1)
 * BMad Agent: Sally (UX), UI Designer
 * 
 * Provides natural, contextual explanations of Emma's capabilities
 * WITHOUT overwhelming users with technical details.
 * 
 * Key UX Principles:
 * - Progressive disclosure (show more on demand)
 * - Category-based organization
 * - Non-technical language
 * - Contextual suggestions based on conversation
 */

import React, { useState } from 'react';
import { 
  ChevronDown, 
  ChevronRight,
  Sparkles,
  Search,
  FileText,
  Languages,
  TrendingUp,
  Users,
  Database,
  Zap,
  BookOpen,
  Mail,
  Lightbulb
} from 'lucide-react';

export interface CapabilityCategory {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  examples: string[];
  isNew?: boolean;
}

// User-friendly capability categories (Sally's UX-approved descriptions)
export const CAPABILITY_CATEGORIES: CapabilityCategory[] = [
  {
    id: 'research',
    name: 'Research & Discovery',
    icon: Search,
    description: "I can search the web, analyze trends, and find the latest information on any topic.",
    examples: [
      "What's the latest news on cloud computing?",
      "Research our competitor's recent announcements",
      "Find market trends for SaaS products in 2026"
    ]
  },
  {
    id: 'content',
    name: 'Content Creation',
    icon: FileText,
    description: "I can help draft emails, documents, presentations, and other business content.",
    examples: [
      "Draft an executive summary for our Q4 report",
      "Help me write a follow-up email to a prospect",
      "Create talking points for our partner meeting"
    ]
  },
  {
    id: 'analysis',
    name: 'Analysis & Insights',
    icon: TrendingUp,
    description: "I can analyze data, identify patterns, and provide strategic recommendations.",
    examples: [
      "Analyze our win rates by region",
      "What patterns do you see in our successful deals?",
      "Compare our approach to industry best practices"
    ]
  },
  {
    id: 'competitive',
    name: 'Competitive Intelligence',
    icon: Users,
    description: "I can research competitors, compare offerings, and identify market positioning.",
    examples: [
      "Compare AWS vs Azure for our use case",
      "What are our competitors saying about AI?",
      "Help me understand Salesforce's latest features"
    ]
  },
  {
    id: 'knowledge',
    name: 'Knowledge Management',
    icon: Database,
    description: "I can search your organization's knowledge base and find relevant information.",
    examples: [
      "What do we know about Acme Corp?",
      "Find our case studies in healthcare",
      "What worked in similar deals before?"
    ]
  },
  {
    id: 'translation',
    name: 'Translation & Localization',
    icon: Languages,
    description: "I can translate content and help adapt messaging for different regions.",
    examples: [
      "Translate this email to German",
      "Help localize our pitch for the Japan market",
      "What cultural considerations for EMEA?"
    ],
    isNew: true
  },
  {
    id: 'verification',
    name: 'Fact Checking',
    icon: Zap,
    description: "I can verify claims, check statistics, and ensure accuracy.",
    examples: [
      "Is it true that AWS has 32% market share?",
      "Verify this statistic about cloud adoption",
      "Check if this competitor claim is accurate"
    ],
    isNew: true
  },
  {
    id: 'learning',
    name: 'Learning & Improvement',
    icon: Lightbulb,
    description: "I learn from every interaction to provide better assistance over time.",
    examples: [
      "Remember that this client prefers brief emails",
      "Learn from our successful proposal templates",
      "What patterns have you noticed in our wins?"
    ],
    isNew: true
  }
];

interface EmmaCapabilitiesExplainerProps {
  className?: string;
  compact?: boolean;
  suggestedCategory?: string;
  onExampleClick?: (example: string) => void;
}

/**
 * Expandable capability category card
 */
function CapabilityCard({ 
  category, 
  isExpanded, 
  onToggle,
  onExampleClick 
}: { 
  category: CapabilityCategory; 
  isExpanded: boolean;
  onToggle: () => void;
  onExampleClick?: (example: string) => void;
}) {
  const Icon = category.icon;
  
  return (
    <div className={`border rounded-lg transition-all ${
      isExpanded 
        ? 'border-emma-300 dark:border-emma-700 bg-emma-50 dark:bg-emma-900/20' 
        : 'border-gray-200 dark:border-gray-700 hover:border-emma-200 dark:hover:border-emma-800'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left"
        aria-expanded={isExpanded}
      >
        <div className={`p-2 rounded-lg ${
          isExpanded 
            ? 'bg-emma-100 dark:bg-emma-800' 
            : 'bg-gray-100 dark:bg-gray-800'
        }`}>
          <Icon className={`w-5 h-5 ${
            isExpanded 
              ? 'text-emma-600 dark:text-emma-400' 
              : 'text-gray-600 dark:text-gray-400'
          }`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              {category.name}
            </h3>
            {category.isNew && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emma-100 text-emma-700 dark:bg-emma-900 dark:text-emma-300 rounded">
                NEW
              </span>
            )}
          </div>
          {!isExpanded && (
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {category.description}
            </p>
          )}
        </div>
        
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 pt-0">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            {category.description}
          </p>
          
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Try asking:
            </p>
            {category.examples.map((example, idx) => (
              <button
                key={idx}
                onClick={() => onExampleClick?.(example)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-emma-300 dark:hover:border-emma-700 hover:bg-emma-50 dark:hover:bg-emma-900/30 transition-colors"
              >
                "{example}"
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact capability badge for inline display
 */
function CapabilityBadge({ 
  category, 
  onClick 
}: { 
  category: CapabilityCategory; 
  onClick?: () => void;
}) {
  const Icon = category.icon;
  
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-emma-100 dark:hover:bg-emma-900/30 hover:text-emma-700 dark:hover:text-emma-300 transition-colors"
    >
      <Icon className="w-3.5 h-3.5" />
      {category.name}
      {category.isNew && (
        <Sparkles className="w-3 h-3 text-amber-500" />
      )}
    </button>
  );
}

/**
 * Main EmmaCapabilitiesExplainer component
 */
export function EmmaCapabilitiesExplainer({
  className = '',
  compact = false,
  suggestedCategory,
  onExampleClick
}: EmmaCapabilitiesExplainerProps) {
  const [expandedId, setExpandedId] = useState<string | null>(suggestedCategory || null);
  
  // Compact mode - just show badges
  if (compact) {
    return (
      <div className={`${className}`}>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          I can help with:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {CAPABILITY_CATEGORIES.map(cat => (
            <CapabilityBadge 
              key={cat.id} 
              category={cat}
              onClick={() => setExpandedId(expandedId === cat.id ? null : cat.id)}
            />
          ))}
        </div>
      </div>
    );
  }
  
  // Full mode with expandable cards
  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-emma-600 dark:text-emma-400" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          What Can Emma Help With?
        </h2>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Just describe what you need in plain language. I'll automatically use the right tools to help you.
      </p>
      
      <div className="space-y-2">
        {CAPABILITY_CATEGORIES.map(cat => (
          <CapabilityCard
            key={cat.id}
            category={cat}
            isExpanded={expandedId === cat.id}
            onToggle={() => setExpandedId(expandedId === cat.id ? null : cat.id)}
            onExampleClick={onExampleClick}
          />
        ))}
      </div>
      
      <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Tip:</strong> You don't need to know which tools to use. 
            Just ask naturally and I'll figure out the best way to help!
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmmaCapabilitiesExplainer;
